import { createApp, h } from 'vue';
import { createInertiaApp, useForm } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';

import { 
    Quasar, Loading, Platform, Notify, 
    Dialog, Cookies, getCssVar, setCssVar,
    SessionStorage,
} from 'quasar';

import iconSet from 'quasar/icon-set/mdi-v7.js';
import '@quasar/extras/mdi-v7/mdi-v7.css';
import 'quasar/dist/quasar.css';

import VueApexCharts from 'vue3-apexcharts';

import axios from 'axios';
import VueAxios from 'vue-axios';

import * as Sentry from "@sentry/vue";

import { useUrlParams } from '@/Composables/useUrlParams.js';

import { useWorkOrderDialogStore } from '@/Stores/Dialogs/WorkOrderDialogStore';

axios.defaults.baseURL = import.meta.env.AXIOS_BASE_URL;
axios.defaults.withCredentials = true;
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

axios.interceptors.response.use(
    (resp) => resp,
    (err) => {
        if (!(err.response?.status ?? false)) {
            return Promise.reject(err);
        }

        if (err.response.status === 401 || err.response.status === 403) {
            Notify.create({
                message: 'You are not authorized to access this page',
                color: 'negative',
            });
        }

        if (err.response.status === 419) {
            Notify.create({
                message: 'Your session has expired\nRedirecting to login page',
                color: 'negative',
            });

            setTimeout(() => {
                window.location.href = '/login';
            }, 2000);
        }
        return Promise.reject(err);
    },
);

import { createPinia } from 'pinia';
const pinia = createPinia();

import globalMixin from './global.mixin';

import '~/sass/app.scss';
import '~/sass/invoice.scss';

createInertiaApp({
    progress: {
        color: '#ff3c00'
    },
    resolve: async (name) =>
        resolvePageComponent(
            `./Pages/${name}.vue`,
            import.meta.glob('./Pages/**/*.vue'),
        ),
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) });

        app.config.globalProperties.$urlParams = useUrlParams();
            
        // Sentry.init({
        //     // debug: true,
        //     app,
        //     dsn: "https://7dc0a50cf6f1ca4302efc6b3b09534dc@o4509181552623616.ingest.us.sentry.io/4509183143772160",

        //     // Adds request headers and IP for users, for more info visit:
        //     // https://docs.sentry.io/platforms/javascript/guides/vue/configuration/options/#sendDefaultPii
        //     sendDefaultPii: true,

        //     integrations: [
        //         Sentry.replayIntegration({
        //             // NOTE: This will disable built-in masking. Only use this if your site has no sensitive data, or if you've already set up other options for masking or blocking relevant data, such as 'ignore', 'block', 'mask' and 'maskFn'.
        //             maskAllText: false,
        //             blockAllMedia: false,
        //         }),

        //         Sentry.feedbackIntegration({
        //             // Additional SDK configuration goes in here, for example:
        //             colorScheme: "system",
        //             autoInject: false,
        //         }),
        //         Sentry.browserTracingIntegration(),
        //         Sentry.vueIntegration({
        //             tracingOptions: {
        //                 trackComponents: true,
        //             },
        //         }),
        //     ],
        //     tracesSampleRate: 1.0,
        //     // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
        //     tracePropagationTargets: ["localhost", /^'https:\/\/lct-lms-dev\.fly\.dev/],
            
        //     replaysSessionSampleRate: 0.1,
        //     replaysOnErrorSampleRate: 1.0,
        // });

        // pinia.use(Sentry.createSentryPiniaPlugin());

        app.mixin(globalMixin)
            .use(pinia)
            .mixin({
                data() {
                    return {
                        darkModeListener: null,
                        workOrderDialogStore: useWorkOrderDialogStore(),
                        // $feedback: Sentry.getFeedback(),
                    };
                },
                methods: {
                    setCssVar,
                    getCssVar,
                    $form: useForm,
                    initDarkModeListener() {
                        if (Cookies.has('preferred-theme')) {
                            this.$q.dark.set(Cookies.get('preferred-theme') === 'dark');
                            return;
                        }

                        if (this.darkModeListener) return;
                        if (!window.matchMedia || !window.matchMedia('(prefers-color-scheme: dark)').matches) return;

                        this.darkModeListener = window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
                            if (Cookies.has('preferred-theme')) {
                                this.$q.dark.set(Cookies.get('preferred-theme') === 'dark');
                                return;
                            }
                        });
                        
                        this.$q.dark.set(window.matchMedia('(prefers-color-scheme: dark)').matches);
                    },
                },
                mounted() {
                    // this.$q.dark.set(true);
                    // setCssVar('primary', '#F976d2');
                    this.initDarkModeListener();
                },
                beforeUnmount() {
                    if (this.darkModeListener ?? false) {
                        this.darkModeListener.removeEventListener('change', this.initDarkModeListener);
                    }
                }
            })
            .use(plugin)
            .use(Quasar, {
                iconSet: iconSet,
                plugins: {
                    Loading,
                    Platform,
                    Notify,
                    Dialog,
                    Cookies,
                    SessionStorage,
                },
                config: {
                    brand: {
                        primary: '#1976d2',
                        secondary: '#00aeef',
                        accent: '#9c27b0',
                        dark: '#1d1d1d',
                        'dark-page': '#121212',

                        // positive: '#21BA45',
                        // negative: '#ff3d1f',
                        // info: '#31CCEC',
                        // warning: '#F2C037',
                    },
                    screen: {
                        bodyClasses: true
                    },
                },
            })
            .use(VueAxios, axios)
            .use(VueApexCharts)
            .mount(el);
    },
});
